import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useUploadDocumentMutation } from "@/hooks/api/document.queries";
import { useState } from "react";
import { UploadCloud } from "lucide-react";

interface DocumentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
}

const DocumentDialog = ({
  isOpen,
  onOpenChange,
  onCancel,
}: DocumentDialogProps) => {
  const uploadMutation = useUploadDocumentMutation();
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFile(null);
      setDocumentType("");
    }
    onOpenChange(open);
  };

  const cancelAndReset = () => {
    setFile(null);
    setDocumentType("");
    onCancel();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !documentType) return;

    uploadMutation.mutate({ file, document_type: documentType }, {
      onSuccess: () => {
        setFile(null);
        setDocumentType("");
        onCancel();
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a secure document for college verification (e.g., ID proof or Marksheet)
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-6">
          
          <div className="space-y-2">
            <Label htmlFor="document_type">Document Type *</Label>
            <Select onValueChange={setDocumentType} value={documentType} required>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="id_proof">ID Proof</SelectItem>
                <SelectItem value="marksheet">Marksheet</SelectItem>
                <SelectItem value="noc">NOC (No Objection Certificate)</SelectItem>
                <SelectItem value="certificate">Certificate</SelectItem>
                <SelectItem value="other">Other Document</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition">
            <UploadCloud size={40} className="text-gray-400 mb-4" />
            <Input 
              type="file" 
              accept="application/pdf, image/jpeg, image/png" 
              onChange={handleFileChange}
              className="max-w-xs cursor-pointer"
              required
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              PDF, JPEG, or PNG supported.
            </p>
          </div>

          <DialogFooter className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancelAndReset}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-primary hover:bg-primary/90" 
              disabled={!file || !documentType || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? "Uploading..." : "Upload Document"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDialog;
